import React, { useMemo } from 'react';
import { extend } from '@thd-nucleus/data-sources';
import {
  PodSection,
  ProductPod,
  ProductImage,
  ProductRating,
  PodFooter,
  PodSpacer,
  ProductATC,
  ProductSponsored,
  PodTapper
} from '@thd-olt-component-react/product-pod';
import proptypes from 'prop-types';
import { ProductHeader } from '@thd-olt-component-react/product-details';
import { Price } from '@thd-olt-component-react/price';
import {
  useRecsCarouselPodTelemetry,
  useNodeRef
} from '../hooks';

const { bool: boolType, string: stringType, number: numType, shape: shapeType, arrayOf: arrayOfType } = proptypes;

export const VerticalRecsProductPodV7 = (props) => {
  const {
    itemId,
    storeId,
    position,
    preferredPriceFlag,
    parent,
    scheme,
    anchorProduct,
    strategy,
    brandTitleMaxLine,
    hideRating,
    hideBadge,
    hideATC,
    noATCFulfillment,
    isMobile,
    onlyReviewCount,
    isMerchRecRequired,
    url,
    product,
    telemetryKey
  } = props;

  const anchorId = (anchorProduct.length > 2) ? anchorProduct : 'n/a';

  const merchRec = useMemo(() => {
    return isMerchRecRequired ? `MERCH=REC-_-${scheme}-_-${anchorId}-_-${position}-_-n/a-_-n/a-_-n/a-_-n/a-_-n/a` : null;
  }, [anchorId, position, isMerchRecRequired, scheme]);

  // nodes
  const [productPodNodeRef, productPodNode] = useNodeRef();

  // -----------------
  // Telemetry
  // -----------------
  const {
    handleClick,
    handleClickOnProductImage,
    handleClickOnProductHeader,
    handleClickOnProductRating,
    handleClickOnProductAtcButton
  } = useRecsCarouselPodTelemetry({
    productPodNode,
    parent,
    title: telemetryKey,
    itemId,
    storeId,
    product,
    position,
    url
  });


  return (
    <div
      ref={productPodNodeRef}
      data-product-id={itemId}
      data-testid="vertical-recs-product-pod-v7"
    >
      <ProductPod
        itemId={itemId}
        storeId={storeId}
        analyticsData={{
          parent, position, scheme, anchorProduct, strategy
        }}
        padding="sui-p-0"
        gap="sui-gap-0"
        render={(pod) => (
          <>
            <a
              className="sui-top-0 sui-left-0 sui-absolute sui-size-full sui-z-1"
              href={url}
              aria-label="Link"
              onClick={handleClick}
            />
            <PodSection noPadding>
              <div className="sui-grid sui-grid-cols-3 sui-gap-2">
                <div className="sui-grid-cols-1">
                  <PodTapper fitWidth zIndex>
                    <ProductImage
                      itemId={pod.itemId}
                      fetchPriority="high"
                      merchQueryParam={merchRec}
                      onClick={handleClickOnProductImage}
                    />
                  </PodTapper>
                </div>
                <div className="sui-col-span-2">
                  <PodSection noPadding columnAlign>
                    <PodTapper fitWidth zIndex>
                      <ProductHeader
                        brand="inline"
                        brandFontSize="sm"
                        itemId={pod.itemId}
                        brandTitleMaxLine={isMobile ? 3 : brandTitleMaxLine}
                        disableShopThisCollection
                        merchQueryParam={merchRec}
                        productNameLineHeight="tight"
                        onClick={handleClickOnProductHeader}
                      />
                    </PodTapper>
                    {!hideRating && (
                      <PodTapper fitWidth zIndex>
                        <ProductRating
                          itemId={pod.itemId}
                          merchQueryParam={merchRec}
                          onlyReviewCount={onlyReviewCount}
                          onClick={handleClickOnProductRating}
                        />
                      </PodTapper>
                    )}
                    <PodTapper zIndex cursorPointer aPointer buttonPointer spanPointer handleClick={handleClick}>
                      <Price
                        itemId={pod.itemId}
                        large={false}
                        storeId={storeId}
                        displayEachUom={false}
                        hideSavingsText={!preferredPriceFlag}
                        showPreferredPricingBadge
                      />
                    </PodTapper>
                    {!hideATC && (
                      <PodFooter noPadding>
                        <PodSpacer minHeight="40px">
                          <PodTapper zIndex>
                            <ProductATC
                              itemId={pod.itemId}
                              checkGeneric
                              outline
                              noATCFulfillment={noATCFulfillment}
                              onClick={handleClickOnProductAtcButton}
                            />
                          </PodTapper>
                        </PodSpacer>
                      </PodFooter>
                    )}
                  </PodSection>
                </div>
              </div>
            </PodSection>
            <div className="sui-flex sui-justify-start sui-recs-pipsem-sponsoredbadge">
              <ProductSponsored itemId={pod.itemId} position={position} noBackground noPadding />
            </div>
          </>
        )}
      />
    </div>
  );
};

VerticalRecsProductPodV7.dataModel = extend(
  ProductPod,
  ProductImage,
  ProductRating,
  Price,
  ProductHeader,
  ProductSponsored
);

VerticalRecsProductPodV7.propTypes = {
  itemId: stringType.isRequired,
  storeId: stringType,
  position: numType,
  parent: stringType,
  preferredPriceFlag: boolType,
  scheme: stringType.isRequired,
  anchorProduct: stringType,
  strategy: stringType,
  brandTitleMaxLine: numType,
  hideRating: boolType,
  hideBadge: boolType,
  hideATC: boolType,
  noATCFulfillment: boolType,
  isMobile: boolType,
  onlyReviewCount: boolType,
  isMerchRecRequired: boolType,
  url: stringType.isRequired,
  product: shapeType({
    itemId: stringType.isRequired,
    identifiers: shapeType({
      canonicalUrl: stringType
    }),
    info: shapeType({
      isSponsored: boolType,
      sponsoredBeacon: shapeType({
        onClickBeacons: arrayOfType(stringType),
        onViewBeacons: arrayOfType(stringType)
      }),
      sponsoredMetadata: shapeType({
        trackSource: stringType,
        campaignId: stringType,
        placementId: stringType,
        slotId: stringType,
        sponsoredId: stringType
      })
    })
  }),
  telemetryKey: stringType
};

VerticalRecsProductPodV7.defaultProps = {
  storeId: '',
  position: null,
  brandTitleMaxLine: 2,
  preferredPriceFlag: false,
  parent: '',
  strategy: 'project-based',
  anchorProduct: '',
  hideRating: false,
  hideBadge: false,
  hideATC: false,
  noATCFulfillment: true,
  isMobile: false,
  onlyReviewCount: true,
  isMerchRecRequired: true
};

VerticalRecsProductPodV7.displayName = 'VerticalRecsProductPodV7';
